import { useRef } from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Separator } from "../../ui/separator";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  MinusCircle,
} from "lucide-react";

const STEP_STATUS_META = {
  complete: {
    label: "Complete",
    className: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
    icon: CheckCircle2,
  },
  needs_work: {
    label: "Needs Work",
    className: "border-amber-500/25 bg-amber-500/10 text-amber-300",
    icon: AlertCircle,
  },
  not_started: {
    label: "Not Started",
    className: "border-border/70 bg-background/40 text-muted-foreground",
    icon: MinusCircle,
  },
};

const FIELD_STATUS_META = {
  complete: {
    label: "Complete",
    dotClassName: "bg-emerald-400",
    textClassName: "text-emerald-300",
  },
  needs_work: {
    label: "Needs Work",
    dotClassName: "bg-amber-400",
    textClassName: "text-amber-300",
  },
  not_started: {
    label: "Not Started",
    dotClassName: "bg-muted-foreground/40",
    textClassName: "text-muted-foreground",
  },
};

export function getWorkflowStepStatus(statusKey) {
  return STEP_STATUS_META[statusKey] ?? STEP_STATUS_META.not_started;
}

export function getWorkflowFieldStatus(statusKey) {
  return FIELD_STATUS_META[statusKey] ?? FIELD_STATUS_META.not_started;
}

export function deriveSimpleFieldStatus({ hasValue, isComplete, needsWork }) {
  if (isComplete) return "complete";
  if (needsWork || hasValue) return "needs_work";
  return "not_started";
}

export function useFieldAnchors() {
  const fieldContainerRefs = useRef({});
  const fieldInputRefs = useRef({});

  const setFieldContainerRef = (fieldKey) => (node) => {
    if (node) {
      fieldContainerRefs.current[fieldKey] = node;
      return;
    }
    delete fieldContainerRefs.current[fieldKey];
  };

  const setFieldInputRef = (fieldKey) => (node) => {
    if (node) {
      fieldInputRefs.current[fieldKey] = node;
      return;
    }
    delete fieldInputRefs.current[fieldKey];
  };

  const focusField = (fieldKey) => {
    const fieldRoot = fieldContainerRefs.current[fieldKey];
    if (!fieldRoot) return;

    fieldRoot.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => {
      const input = fieldInputRefs.current[fieldKey];
      if (input instanceof HTMLElement) {
        input.focus({ preventScroll: true });
      }
    }, 180);
  };

  return {
    setFieldContainerRef,
    setFieldInputRef,
    focusField,
  };
}

export function WorkflowStepSection({
  stepNumber,
  title,
  description,
  status,
  actions,
  children,
  noDivider = false,
}) {
  const statusMeta = getWorkflowStepStatus(status);
  const StatusIcon = statusMeta.icon;

  return (
    <section className={`space-y-5 ${noDivider ? "" : "pb-7"}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {stepNumber ? (
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                  STEP {stepNumber}
                </p>
              ) : null}
              <span className="text-muted-foreground/60" aria-hidden="true">
                -
              </span>
              <h4 className="font-heading text-lg font-semibold leading-none">{title}</h4>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${statusMeta.className}`}>
              <StatusIcon className="h-3.5 w-3.5" />
              {statusMeta.label}
            </span>
          </div>
          {description ? <p className="max-w-3xl text-xs leading-6 text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2 pt-0.5">{actions}</div> : null}
      </div>
      {children}
      {!noDivider ? <Separator className="bg-border/50" /> : null}
    </section>
  );
}

export function WorkflowStepProgressSummary({ title = "Progress", items, testId }) {
  return (
    <div className="rounded-xl border border-border/70 bg-background/30 p-3" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{title}</p>
        {items.map((item) => {
          const statusMeta = getWorkflowStepStatus(item.status);
          const StatusIcon = statusMeta.icon;
          return (
            <div
              key={item.key ?? item.number ?? item.title}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${statusMeta.className}`}
              data-testid={item.testId}
            >
              <StatusIcon className="h-3.5 w-3.5" />
              {item.number ? <span>Step {item.number}</span> : null}
              <span>{statusMeta.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WorkflowField({
  fieldKey,
  label,
  helperText,
  hintText,
  metadata,
  status,
  containerRef,
  error,
  children,
  className = "",
}) {
  const fieldStatus = getWorkflowFieldStatus(status);

  return (
    <div
      id={`problem-field-${fieldKey}`}
      ref={containerRef}
      data-problem-field={fieldKey}
      className={`space-y-2.5 scroll-mt-24 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={`problem-input-${fieldKey}`}>{label}</Label>
          <span className={`inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider ${fieldStatus.textClassName}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${fieldStatus.dotClassName}`} aria-hidden="true" />
            {fieldStatus.label}
          </span>
        </div>
        {metadata ? <span className="text-[11px] font-medium text-muted-foreground/80">{metadata}</span> : null}
      </div>
      {children}
      {helperText ? <p className="text-[11px] leading-5 text-muted-foreground/90">{helperText}</p> : null}
      {hintText ? <p className="text-[11px] leading-5 text-muted-foreground/75">{hintText}</p> : null}
      {error ? <p className="text-xs text-red-400" data-testid={`${fieldKey}-error`}>{error}</p> : null}
    </div>
  );
}

export function StickyFormActionBar({
  primaryLabel,
  onPrimaryClick,
  secondaryLabel,
  onSecondaryClick,
  secondaryDisabled,
  secondaryLoadingLabel,
}) {
  return (
    <div className="sticky bottom-0 z-10 -mx-5 mt-6 border-t border-border/60 bg-gradient-to-t from-card via-card/95 to-card/85 px-5 pb-5 pt-4 backdrop-blur supports-[backdrop-filter]:bg-card/90 lg:pb-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          onClick={onSecondaryClick}
          disabled={secondaryDisabled}
          className="min-w-[132px]"
        >
          {secondaryDisabled ? secondaryLoadingLabel : secondaryLabel}
        </Button>
        <Button onClick={onPrimaryClick} className="min-w-[220px] shadow-sm">
          {primaryLabel}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function AssistanceToolCard({ title, description, action, children }) {
  return (
    <div className="flex h-full flex-col rounded-xl border border-border/70 bg-background/35 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/50 pb-3">
        <div className="min-w-0 space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/80">Optional Tool</p>
          <h4 className="font-heading text-sm font-semibold">{title}</h4>
          <p className="max-w-md text-[11px] leading-5 text-muted-foreground/85">{description}</p>
        </div>
        <div className="shrink-0 pt-0.5">{action}</div>
      </div>
      <div className="flex-1 pt-4">{children}</div>
    </div>
  );
}
